import { useState } from "react";
import { CheckCircle2, Clock, Loader2, Mail } from "lucide-react";
import { api, fieldErrorsFrom422, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/site/SiteLayout";

const EMPTY = { name: "", email: "", project_type: "", message: "", website: "" };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const TYPES = ["New website", "Web application", "E-commerce", "SaaS / product", "Performance & SEO", "Care plan / support", "Something else"];

const inputCls = (err) =>
  `w-full rounded-lg border bg-[#0B0F18] px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none transition-colors duration-200 focus:border-cyan-400/70 focus:ring-2 focus:ring-cyan-400/20 ${err ? "border-red-500/70" : "border-white/10"}`;

const validate = (f) => {
  const e = {};
  if (!f.name.trim()) e.name = "Please enter your name";
  if (!f.email.trim()) e.email = "Please enter your email";
  else if (!EMAIL_RE.test(f.email.trim())) e.email = "Please enter a valid email";
  if (!f.message.trim()) e.message = "Please enter a message";
  return e;
};

const Field = ({ id, label, error, children }) => (
  <div>
    <label htmlFor={id} className="mb-2 block text-sm font-medium text-slate-300">{label}</label>
    {children}
    {error && <p data-testid={`contact-${id}-error`} className="mt-1.5 text-xs text-red-400">{error}</p>}
  </div>
);

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [serverError, setServerError] = useState("");
  const set = (k) => (e) => { setForm({ ...form, [k]: e.target.value }); setErrors({ ...errors, [k]: undefined }); };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    setErrors(errs);
    setServerError("");
    if (Object.keys(errs).length) return;
    setStatus("sending");
    try {
      await api.post("/contact", { ...form, project_type: form.project_type || null });
      setForm(EMPTY);
      setStatus("sent");
    } catch (err) {
      setStatus("idle");
      const fe = fieldErrorsFrom422(err.response?.data?.detail);
      if (fe.email) fe.email = "Please enter a valid email";
      if (Object.keys(fe).length) setErrors(fe);
      else setServerError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHeader eyebrow="// contact" title="Let's talk about your project." testId="contact-title">
        Share a few details and we'll reply within one business day with next steps, a rough budget range and timeline.
      </PageHeader>
      <section className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 md:py-24 lg:grid-cols-[1fr_1.4fr] lg:px-12">
        <aside className="space-y-6">
          <div className="card-tech rounded-2xl p-6">
            <Mail className="h-5 w-5 text-cyan-300" />
            <p className="mt-4 text-sm text-slate-400">Prefer email?</p>
            <a href="mailto:Brent@bealldigitalgroup.com" data-testid="contact-direct-email" className="mt-1 block break-all font-medium text-white transition-colors hover:text-cyan-300">
              Brent@bealldigitalgroup.com
            </a>
          </div>
          <div className="card-tech rounded-2xl p-6">
            <Clock className="h-5 w-5 text-cyan-300" />
            <p className="mt-4 font-medium text-white">Response within 1 business day</p>
            <p className="mt-1 text-sm text-slate-400">Monday to Friday, US Eastern time.</p>
          </div>
        </aside>
        <div className="card-tech rounded-2xl p-6 sm:p-8">
          {status === "sent" && (
            <div data-testid="contact-success-message" role="status" className="mb-6 flex items-start gap-3 rounded-xl border border-emerald-400/30 bg-emerald-400/10 p-4 text-sm text-emerald-200">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" /> Thanks! We'll be in touch within 1 business day.
            </div>
          )}
          <form data-testid="contact-form" noValidate onSubmit={submit} className="space-y-5">
            <div className="grid gap-5 sm:grid-cols-2">
              <Field id="name" label="Name" error={errors.name}>
                <input id="name" data-testid="contact-input-name" value={form.name} onChange={set("name")} placeholder="Jane Smith" className={inputCls(errors.name)} />
              </Field>
              <Field id="email" label="Email" error={errors.email}>
                <input id="email" type="email" data-testid="contact-input-email" value={form.email} onChange={set("email")} placeholder="jane@company.com" className={inputCls(errors.email)} />
              </Field>
            </div>
            <Field id="project_type" label="Project type (optional)">
              <select id="project_type" data-testid="contact-input-subject" value={form.project_type} onChange={set("project_type")} className={inputCls(false)}>
                <option value="">Select a project type</option>
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field id="message" label="Message" error={errors.message}>
              <textarea id="message" rows={6} data-testid="contact-input-message" value={form.message} onChange={set("message")} placeholder="What are you building, and when do you need it?" className={inputCls(errors.message)} />
            </Field>
            <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} className="hidden" aria-hidden="true" />
            {serverError && <p data-testid="contact-server-error" className="text-sm text-red-400">{serverError}</p>}
            <button type="submit" data-testid="contact-submit-button" disabled={status === "sending"}
              className="btn-glow inline-flex w-full items-center justify-center gap-2 rounded-full bg-cyan-400 px-7 py-3.5 text-sm font-semibold text-[#07090E] hover:bg-cyan-300 disabled:opacity-60 sm:w-auto">
              {status === "sending" && <Loader2 className="h-4 w-4 animate-spin" />} Send message
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
